(function () {
  'use strict';

  var STORAGE_KEY   = 'pn_state';
  var ANCHOR_KEY    = 'pn_anchor';
  var PLAYER_ID     = 'pn-player';
  var DEEZER_WORKER  = 'https://restless-sun-4aca.rapid-shadow-9cc8.workers.dev/';
  var ITUNES_SEARCH  = 'https://itunes.apple.com/search';

  // Normalise a string for fuzzy comparison: lowercase, strip punctuation/diacritics
  function normStr(s) {
    return (s || '').toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9 ]/g, ' ')
      .replace(/\s+/g, ' ').trim();
  }

  // Score how well two strings match (0–1)
  function matchScore(a, b) {
    var na = normStr(a), nb = normStr(b);
    if (!na || !nb) return 0;
    if (na === nb) return 1;
    if (na.indexOf(nb) !== -1 || nb.indexOf(na) !== -1) return 0.85;
    // word-overlap ratio
    var wa = na.split(' '), wb = nb.split(' ');
    var common = wa.filter(function(w) { return w.length > 2 && wb.indexOf(w) !== -1; }).length;
    return common / Math.max(wa.length, wb.length);
  }

  // Fetch a 30-second preview from Apple Music / iTunes for artist + album
  function fetchItunesPreview(artist, album, trackTitle, cb) {
    var isVarious = /^various/i.test(artist);
    var query = isVarious ? album : (artist + ' ' + album);
    var baseParams = '?term=' + encodeURIComponent(query) + '&media=music&entity=song&limit=25';
    var url = ITUNES_SEARCH + baseParams + '&country=us';
    fetch(url)
      .then(function(r) { return r.json(); })
      .then(function(data) {
        var previewCheck = (data.results || []).filter(function(r) { return r.previewUrl && r.kind === 'song'; });
        if (!previewCheck.length) {
          return fetch(ITUNES_SEARCH + baseParams + '&country=ca')
            .then(function(r2) { return r2.json(); });
        }
        return data;
      })
      .then(function(data) {
        var results = (data.results || []).filter(function(r) {
          return r.previewUrl && r.kind === 'song';
        });
        if (!results.length) { cb(''); return; }

        // Score each result against artist + album
        var scored = results.map(function(r) {
          var artistScore = isVarious ? 0.5 : matchScore(r.artistName, artist);
          var albumScore  = matchScore(r.collectionName, album);
          var trackScore  = trackTitle ? matchScore(r.trackName, trackTitle) : 0;
          return { r: r, score: artistScore * 0.4 + albumScore * 0.5 + trackScore * 0.1 };
        });
        scored.sort(function(a, b) { return b.score - a.score; });

        var best = scored[0];
        // Require strong album match and at least a reasonable artist match
        // to avoid playing a completely wrong record
        var albumScore  = matchScore(best.r.collectionName, album);
        var artistScore = isVarious ? 1 : matchScore(best.r.artistName, artist);
        if (albumScore >= 0.55 && artistScore >= 0.4) {
          cb(best.r.previewUrl);
        } else {
          cb('');
        }
      })
      .catch(function() { cb(''); });
  }

  // Fetch all tracks from an album on iTunes, returned as an array of track objects
  function fetchItunesAlbum(artist, album, cb) {
    var isVarious = /^various/i.test(artist);
    var query = isVarious ? album : (artist + ' ' + album);
    var baseParams = '?term=' + encodeURIComponent(query) + '&media=music&entity=song&limit=50';
    // Try US store first; fall back to CA (many European labels only appear there)
    var url = ITUNES_SEARCH + baseParams + '&country=us';
    fetch(url)
      .then(function(r) { return r.json(); })
      .then(function(data) {
        var previewCheck = (data.results || []).filter(function(r) { return r.previewUrl && r.kind === 'song'; });
        if (!previewCheck.length) {
          return fetch(ITUNES_SEARCH + baseParams + '&country=ca')
            .then(function(r2) { return r2.json(); });
        }
        return data;
      })
      .then(function(data) {
        // All songs from query — used to include tracks that lack a previewUrl
        var allSongs = (data.results || []).filter(function(r) {
          return r.kind === 'song';
        });
        // Only songs with previews are used for album-matching/scoring
        var previewSongs = allSongs.filter(function(r) { return r.previewUrl; });
        if (!previewSongs.length) { cb([]); return; }

        var scored = previewSongs.map(function(r) {
          var artistScore = isVarious ? 0.5 : matchScore(r.artistName, artist);
          var albumScore  = matchScore(r.collectionName, album);
          return { r: r, albumScore: albumScore, artistScore: artistScore };
        });
        scored.sort(function(a, b) {
          return (b.albumScore + b.artistScore) - (a.albumScore + a.artistScore);
        });

        var best = scored[0];
        if (best.albumScore < 0.55 || (!isVarious && best.artistScore < 0.4)) { cb([]); return; }

        // Gather ALL tracks for this collectionId — including those without a preview
        var collectionId = best.r.collectionId;
        var albumTracks = allSongs.filter(function(r) {
          return r.collectionId === collectionId;
        });
        albumTracks.sort(function(a, b) {
          if (a.discNumber !== b.discNumber) return a.discNumber - b.discNumber;
          return a.trackNumber - b.trackNumber;
        });

        cb(albumTracks.map(function(r) {
          return {
            title:       r.trackName,
            side:        String(r.trackNumber),
            artist:      r.artistName,
            record:      r.collectionName,
            url:         r.previewUrl || '',
            unavailable: !r.previewUrl,
            duration:    ''
          };
        }));
      })
      .catch(function() { cb([]); });
  }

  // ── State ─────────────────────────────────────────────────────────────
  var queue        = [];
  var currentIndex = -1;
  var isPlaying    = false;

  // ── Audio element ─────────────────────────────────────────────────────
  var audio = new Audio();
  audio.preload = 'metadata';

  // iOS Safari blocks audio.play() from async callbacks.
  // On first touch, play a 1-sample silent WAV through AudioContext to unlock
  // audio for subsequent async play() calls (Deezer URL re-fetches, session restore).
  (function () {
    document.addEventListener('touchstart', function () {
      if (audio._unlocked) return;
      audio._unlocked = true;
      var AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      try {
        var ac = new AudioCtx();
        ac.resume().catch(function () {});
        var buf = ac.createBuffer(1, 1, 22050);
        var src = ac.createBufferSource();
        src.buffer = buf;
        src.connect(ac.destination);
        src.start(0);
      } catch (e) {}
    }, { once: true, passive: true });
  }());

  // ── Canvas / visualizer ───────────────────────────────────────────────
  var canvas, ctx, rafId;

  function resizeCanvas() {
    if (!canvas) return;
    var dpr = window.devicePixelRatio || 1;
    canvas.width  = canvas.offsetWidth  * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
  }

  function drawFrame(ts) {
    rafId = requestAnimationFrame(drawFrame);
    if (!ctx || !canvas) return;

    var W = canvas.offsetWidth;
    var H = canvas.offsetHeight;
    ctx.clearRect(0, 0, W, H);

    var progress = audio.duration ? audio.currentTime / audio.duration : 0;
    var playX    = progress * W;

    if (!isPlaying && progress === 0) {
      // Idle: single dim centre line
      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, H / 2);
      ctx.lineTo(W, H / 2);
      ctx.stroke();
      return;
    }

    var t = ts * 0.001;

    // Helper: draw one sine layer, clipped to a horizontal band
    function drawLayer(alpha, freq, amp, speed, freq2, amp2, speed2) {
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(255,255,255,' + alpha + ')';
      ctx.lineWidth = 1;
      for (var x = 0; x <= W; x++) {
        var n = x / W;
        var y = H / 2
          + Math.sin(n * Math.PI * freq  + t * speed)  * H * amp
          + Math.sin(n * Math.PI * freq2 + t * speed2) * H * amp2;
        if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }

    // Played section — brighter
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, playX, H);
    ctx.clip();
    drawLayer(0.55, 3.4,  0.30, 0.65, 0,    0,    0);
    drawLayer(0.40, 8.5,  0.18, 1.75, 2.8,  0.10, 0.48);
    drawLayer(0.20, 19,   0.07, 3.1,  13,   0.05, 2.2);
    ctx.restore();

    // Unplayed section — dim
    ctx.save();
    ctx.beginPath();
    ctx.rect(playX, 0, W - playX, H);
    ctx.clip();
    drawLayer(0.08, 3.4,  0.30, 0.65, 0,    0,    0);
    drawLayer(0.12, 8.5,  0.18, 1.75, 2.8,  0.10, 0.48);
    drawLayer(0.05, 19,   0.07, 3.1,  13,   0.05, 2.2);
    ctx.restore();

    // Playhead line
    if (progress > 0 && progress < 1) {
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      ctx.lineWidth = 1;
      ctx.moveTo(playX, 0);
      ctx.lineTo(playX, H);
      ctx.stroke();
    }
  }

  function startViz() {
    resizeCanvas();
    if (!rafId) rafId = requestAnimationFrame(drawFrame);
  }

  // ── DOM refs ──────────────────────────────────────────────────────────
  var player;
  var btnPlay, btnPrev, btnNext, pitchSlider, pitchRateLabel;
  var elSide, elTitle, elMeta, elTime;
  var btnPlayCompact, elCompactTitle;

  // ── Helpers ───────────────────────────────────────────────────────────
  function formatTime(s) {
    if (!s || isNaN(s)) return '0:00';
    var m = Math.floor(s / 60);
    var sec = Math.floor(s % 60);
    return m + ':' + (sec < 10 ? '0' : '') + sec;
  }

  // ── Session state ─────────────────────────────────────────────────────
  function persist() {
    try {
      // Strip Deezer CDN URLs before saving — they expire and are always re-fetched.
      var cleanQueue = queue.map(function (t) {
        if (t.url && t.url.indexOf('dzcdn.net') !== -1) {
          return Object.assign({}, t, { url: '' });
        }
        return t;
      });
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        queue:  cleanQueue,
        index:  currentIndex,
        time:   audio.currentTime,
        pitch:  audio.playbackRate,
      }));
    } catch (_) {}
  }

  function setPitch(rate) {
    rate = Math.max(0.92, Math.min(1.08, rate));
    audio.playbackRate = rate;
    if (pitchSlider) pitchSlider.value = rate;
    if (pitchRateLabel) {
      var pct = ((rate - 1) * 100);
      pitchRateLabel.textContent = (pct >= 0 ? '+' : '') + pct.toFixed(1) + '%';
    }
  }

  function restore() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var s = JSON.parse(raw);
      if (!s || !s.queue || !s.queue.length) return;

      queue        = s.queue;
      currentIndex = s.index;
      setPitch(s.pitch || 1.0);

      var track = queue[currentIndex];
      if (!track) return;

      if (track.url) {
        audio.src = track.url;
        audio.load();
        if (s.time > 1) {
          audio.addEventListener('loadedmetadata', function () {
            audio.currentTime = s.time;
          }, { once: true });
        }
      }
      updateInfo();
      showPlayer();
    } catch (_) {}
  }

  // ── UI helpers ────────────────────────────────────────────────────────
  function showPlayer() {
    if (!player) return;
    // Apply anchor + minimized state before the slide-up so the player is
    // already at the correct position and size when the animation runs.
    try {
      var savedAnchor = sessionStorage.getItem(ANCHOR_KEY);
      var savedMin    = sessionStorage.getItem('pn_minimized');
      if (savedAnchor === 'left' || savedAnchor === 'right' || savedMin === '1') {
        player.style.transition = 'none';
        if (savedMin === '1')                                    player.classList.add('is-minimized');
        if (savedAnchor === 'left' || savedAnchor === 'right')   applyAnchor(savedAnchor, false);
        void player.offsetHeight; // force reflow so size + position are committed
        player.style.transition = '';
      }
    } catch (_) {}
    player.classList.add('is-visible');
    document.body.classList.add('pn-active');
  }

  function syncPlayBtn() {
    var state = isPlaying ? 'playing' : 'paused';
    var label = isPlaying ? 'Pause' : 'Play';
    if (btnPlay) { btnPlay.dataset.state = state; btnPlay.setAttribute('aria-label', label); }
    if (btnPlayCompact) { btnPlayCompact.dataset.state = state; btnPlayCompact.setAttribute('aria-label', label); }
    if (player) player.classList.toggle('is-playing', isPlaying);
  }

  function updateInfo() {
    var t = queue[currentIndex];
    if (!t) return;
    if (elSide)  elSide.textContent  = t.side  || '';
    if (elTitle) elTitle.textContent = t.title || '';
    if (elMeta)  elMeta.textContent  = [t.artist, t.record].filter(Boolean).join(' — ');
    if (elCompactTitle) elCompactTitle.textContent = t.title || '';

    document.querySelectorAll('.pn-track-trigger').forEach(function (btn) {
      var urlMatch   = btn.dataset.url && t.url && btn.dataset.url === t.url;
      var titleMatch = btn.dataset.title === t.title && btn.dataset.side === t.side && btn.dataset.record === t.record;
      btn.classList.toggle('is-playing', (urlMatch || titleMatch) && isPlaying);
    });
  }

  function updateSeek() {
    if (elTime) {
      var total = audio.duration;
      elTime.textContent = total
        ? formatTime(audio.currentTime) + ' / ' + formatTime(total)
        : formatTime(audio.currentTime);
    }
  }

  // ── Playback ──────────────────────────────────────────────────────────
  function doPlay() {
    audio.play().then(function () {
      isPlaying = true;
      syncPlayBtn();
      updateInfo();
      persist();
    }).catch(function () {});
  }

  function doPause() {
    audio.pause();
    isPlaying = false;
    syncPlayBtn();
    updateInfo();
    persist();
  }

  function loadTrack(index, autoplay) {
    if (!queue[index]) return;
    currentIndex = index;
    updateInfo();
    showPlayer();
    persist();
    resolveUrl(queue[index], function (url) {
      if (!url) {
        // No preview available — stop whatever was playing so the old track
        // doesn't continue playing under the new track's UI.
        audio.pause();
        audio.src = '';
        isPlaying = false;
        syncPlayBtn();
        return;
      }
      audio.src = url;
      audio.load();
      if (autoplay) doPlay(); else syncPlayBtn();
    });
  }

  function playNext() {
    if (currentIndex < queue.length - 1) loadTrack(currentIndex + 1, true);
  }

  function playPrev() {
    if (audio.currentTime > 3) audio.currentTime = 0;
    else if (currentIndex > 0) loadTrack(currentIndex - 1, true);
    else audio.currentTime = 0;
  }

  // ── Public API ────────────────────────────────────────────────────────
  window.ParanoisePlayer = {
    load: function (tracks, startIndex) {
      queue = tracks.slice();
      loadTrack(startIndex || 0, true);
    },
    play: function (track) {
      var idx = queue.findIndex(function (t) {
        return (track.url && t.url === track.url) ||
               (t.title === track.title && t.side === track.side && t.record === track.record);
      });
      if (idx !== -1 && idx === currentIndex) {
        isPlaying ? doPause() : doPlay();
        return;
      }
      if (idx !== -1) loadTrack(idx, true);
      else { queue.push(track); loadTrack(queue.length - 1, true); }
    },
    enqueue: function (tracks) {
      queue = queue.concat(tracks);
      persist();
    },
  };

  // ── Custom events ─────────────────────────────────────────────────────
  document.addEventListener('paranoise:play',  function (e) {
    window.ParanoisePlayer.play(e.detail);
  });
  document.addEventListener('paranoise:queue', function (e) {
    window.ParanoisePlayer.load(e.detail.tracks, e.detail.index || 0);
  });

  // ── Catalog & overrides ───────────────────────────────────────────────
  var catalog   = null;
  var overrides = null;

  function fetchCatalog(cb) {
    if (catalog) { cb(catalog); return; }
    var url = window.ParanoiseCatalogUrl || '/cdn/shop/t/1/assets/paranoise-catalog.json';
    var xhr = new XMLHttpRequest();
    xhr.open('GET', url);
    xhr.onload = function () {
      if (xhr.status === 200) {
        try { catalog = JSON.parse(xhr.responseText); } catch (_) { catalog = {}; }
      } else {
        catalog = {};
      }
      cb(catalog);
    };
    xhr.onerror = function () { catalog = {}; cb(catalog); };
    xhr.send();
  }

  function fetchOverrides(cb) {
    if (overrides) { cb(overrides); return; }
    var url = window.ParanoiseOverridesUrl || '/cdn/shop/t/1/assets/paranoise-overrides.json';
    var xhr = new XMLHttpRequest();
    xhr.open('GET', url);
    xhr.onload = function () {
      if (xhr.status === 200) {
        try { overrides = JSON.parse(xhr.responseText); } catch (_) { overrides = {}; }
      } else {
        overrides = {};
      }
      cb(overrides);
    };
    xhr.onerror = function () { overrides = {}; cb(overrides); };
    xhr.send();
  }

  function normKey(s) {
    return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  // Loose word-level similarity used only for the catalog artist gate.
  function _artistSim(a, b) {
    var na = (a || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
    var nb = (b || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
    if (!na || !nb) return 0;
    if (na === nb) return 1;
    if (na.indexOf(nb) !== -1 || nb.indexOf(na) !== -1) return 0.85;
    var wa = na.split(' ').filter(function (w) { return w.length > 1; });
    var wb = nb.split(' ').filter(function (w) { return w.length > 1; });
    var wbSet = {};
    wb.forEach(function (w) { wbSet[w] = true; });
    var common = 0;
    wa.forEach(function (w) { if (wbSet[w]) common++; });
    return common / Math.max(wa.length, wb.length, 1);
  }

  /**
   * Catalog lookup with priority order:
   *   1. vendor+title key  — most specific, always trusted
   *   2. handle key        — Shopify handle is unambiguous
   *   3. title-only key    — only accepted when vendor/artist similarity >= 0.40
   */
  function lookupCatalog(cat, title, vendor, handle, artist) {
    if (!cat) return null;

    var vtKey = normKey((vendor || '') + title);
    if (vtKey.length >= 2 && cat[vtKey]) return cat[vtKey];

    // Also try artist+album key (artist parsed from title, not the label)
    if (artist) {
      var atKey = normKey(artist + title);
      if (atKey.length >= 2 && cat[atKey]) return cat[atKey];
    }

    if (handle) {
      var hKey = normKey(handle);
      if (hKey.length >= 2 && cat[hKey]) return cat[hKey];
    }

    var tKey = normKey(title);
    if (tKey.length >= 2 && cat[tKey]) {
      var candidate = cat[tKey];
      // Accept if either the parsed artist OR vendor matches the catalog artist
      if (candidate.artist) {
        var artistOk = (artist && _artistSim(artist, candidate.artist) >= 0.40)
                    || (vendor && _artistSim(vendor, candidate.artist) >= 0.40);
        if (!artistOk) return null;
      }
      return candidate;
    }

    return null;
  }

  function isPlayableUrl(url) {
    if (!url) return false;
    if (url.indexOf('.p.m4a') !== -1) return false;
    return true;
  }

  function resolveUrl(track, cb) {
    var url = track.url;
    // Deezer CDN URLs expire — re-fetch unless freshly resolved (within 10 min).
    // Keeps play() in the synchronous gesture chain when the URL is still valid.
    if (url && url.indexOf('dzcdn.net') !== -1) {
      if (track._fetchedAt && Date.now() - track._fetchedAt < 600000) { cb(url); return; }
      url = ''; // stale — fall through to re-fetch
    }
    // Any other URL (iTunes preview, Bandcamp, etc.) is stable — use directly.
    // Calling cb() synchronously here keeps audio.play() inside the iOS gesture chain.
    if (url) { cb(url); return; }

    // No usable URL — ask the Worker, then fall back to iTunes
    var params = 'artist=' + encodeURIComponent(track.artist || '')
      + '&album='  + encodeURIComponent(track.record  || '')
      + '&track='  + encodeURIComponent(track.title   || '');
    fetch(DEEZER_WORKER + '?' + params)
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (d.preview) { track.url = d.preview; cb(d.preview); return; }
        fetchItunesPreview(track.artist || '', track.record || '', track.title || '', function(itunesUrl) {
          if (itunesUrl) track.url = itunesUrl;
          cb(itunesUrl || '');
        });
      })
      .catch(function () { cb(''); });
  }

  // Fetch the full tracklist for a release directly from the Discogs public API.
  // Returns tracks via cb(tracks[], albumTitle, artistName). All tracks have url:''
  // and unavailable:true — caller should run resolveTracklistAvailability on them.
  function fetchDiscogsTracklist(releaseId, cb) {
    if (!releaseId) { cb([], '', ''); return; }
    fetch('https://api.discogs.com/releases/' + releaseId, {
      headers: { 'User-Agent': 'ParanoiseShopify/1.0 +https://paranoise.store' }
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var releaseArtists = (data.artists || [])
          .map(function (a) { return (a.name || '').replace(/\s*\(\d+\)\s*$/, '').trim(); })
          .join(', ');
        var albumTitle = data.title || '';

        var tracks = (data.tracklist || [])
          // Accept explicit 'track' type and entries without a type (many releases omit it)
          // Exclude headings and index entries only
          .filter(function (t) { return t.type_ !== 'heading' && t.type_ !== 'index'; })
          .map(function (t) {
            var trackArtist = (t.artists || [])
              .map(function (a) { return (a.name || '').replace(/\s*\(\d+\)\s*$/, '').trim(); })
              .join(', ') || releaseArtists;
            return {
              side:        t.position || '',
              title:       t.title    || '',
              duration:    t.duration || '',
              artist:      trackArtist,
              record:      albumTitle,
              url:         '',
              unavailable: true,
            };
          })
          .filter(function (t) {
            if (!t.title) return false;
            // Drop entries whose title is purely a duration string (M:SS / MM:SS) —
            // some releases store duration as a separate tracklist entry
            if (/^\d{1,2}:\d{2}$/.test(t.title.trim())) return false;
            // Drop entries where both position AND title are purely numeric —
            // these are CD/data track artifacts that appear in multi-format releases
            var numPos   = /^\s*\d+\s*$/.test(t.side);
            var numTitle = /^\s*\d+\s*$/.test(t.title);
            return !(numPos && numTitle);
          });

        cb(tracks, albumTitle, releaseArtists);
      })
      .catch(function () { cb([], '', ''); });
  }

  // Extract a tracklist from a plain-text Discogs-style product description.
  // Handles formats like: "Track List: A1. Title, B1. Title" or line-per-track.
  function parseDescriptionTracks(desc, artist, album) {
    if (!desc) return [];
    var skipWords = /^(genre|style|media\s*condition|sleeve\s*condition|media|sleeve|label|cat|year|format|condition|notes|barcode|matrix|pressing|country|released|data\s+provided)/i;
    var tracks = [];

    // Grab everything after "Track List:" / "Tracklist:" if present
    var tlIdx = desc.search(/track\s*list\s*:?\s*/i);
    var text  = tlIdx >= 0 ? desc.slice(tlIdx) : desc;
    text = text.replace(/track\s*list\s*:?\s*/i, '');

    // Flatten: ||| markers and newlines → spaces; collapse whitespace
    text = text.replace(/\|\|\|/g, ' ').replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();

    // Truncate at the first non-track section
    var stopIdx = text.search(/\b(media\s+condition|sleeve\s+condition|media:|sleeve:|notes:|barcode|matrix|pressing|country:|released:|data\s+provided)/i);
    if (stopIdx > 0) text = text.slice(0, stopIdx).trim();

    // Split at each track-identifier boundary so every chunk starts with its own side label.
    // Lookahead keeps the identifier attached to the chunk that follows it.
    var chunks = text.split(/(?=\b[A-Z]{0,2}\d+\s*[.\-:])/i);

    var trackRe = /^([A-Z]{0,2}\d+)\s*[.\-:]\s*(.{2,})/i;

    chunks.forEach(function (chunk) {
      chunk = chunk.trim().replace(/,\s*$/, '').trim();
      var m = chunk.match(trackRe);
      if (!m) return;
      var side  = m[1].toUpperCase();
      var title = m[2].trim()
        .replace(/\s*[\(\[]\d{1,2}:\d{2}(?::\d{2})?[\)\]]\s*$/, '')
        .replace(/\s+\d{1,2}:\d{2}(?::\d{2})?\s*$/, '')
        .replace(/,\s*$/, '')
        .trim();
      if (title.length < 2 || skipWords.test(title)) return;
      // Skip entries where a bare numeric position paired with a 2-digit title —
      // these are duration strings (e.g. "7:26") mis-parsed as track entries
      if (/^\d{1,2}$/.test(side) && /^\d{2}$/.test(title)) return;
      tracks.push({ side: side, title: title, artist: artist, record: album, url: '', unavailable: true });
    });
    return tracks;
  }

  // For catalog entries where all URLs are empty, proactively test whether previews
  // exist (iTunes then Deezer) so we can accurately show/hide play buttons before
  // the user has to click.
  function resolveTracklistAvailability(tracks, artist, album, cb) {
    fetchItunesAlbum(artist, album, function (itunesTracks) {
      if (itunesTracks.length) {
        var resolved = tracks.map(function (t) {
          var best = null, bestScore = 0;
          itunesTracks.forEach(function (it) {
            var s = matchScore(it.title, t.title);
            if (s > bestScore) { bestScore = s; best = it; }
          });
          if (best && bestScore >= 0.55) {
            return Object.assign({}, t, { url: best.url || '', unavailable: !best.url });
          }
          return Object.assign({}, t, { unavailable: true });
        });
        cb(resolved);
        return;
      }

      // iTunes has no trace of this album — ask Worker for all track previews at once.
      // The all=1 mode returns every Deezer preview URL for the album in one call so
      // buttons get real URLs embedded up-front (iOS requires synchronous audio.play()).
      var allParams = 'artist=' + encodeURIComponent(artist)
        + '&album=' + encodeURIComponent(album) + '&all=1';
      fetch(DEEZER_WORKER + '?' + allParams)
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var wTracks = d.tracks || [];
          if (wTracks.length) {
            var ts = Date.now();
            var resolved = tracks.map(function (t) {
              var best = null, bestScore = 0;
              wTracks.forEach(function (wt) {
                var s = matchScore(wt.title, t.title);
                if (s > bestScore) { bestScore = s; best = wt; }
              });
              if (best && bestScore >= 0.4 && best.preview) {
                return Object.assign({}, t, { url: best.preview, unavailable: false, _fetchedAt: ts });
              }
              return Object.assign({}, t, { unavailable: true });
            });
            cb(resolved);
          } else {
            cb(tracks.map(function (t) { return Object.assign({}, t, { unavailable: true }); }));
          }
        })
        .catch(function () {
          cb(tracks.map(function (t) { return Object.assign({}, t, { unavailable: true }); }));
        });
    });
  }

  function autoLoadProductTracks() {
    var el = document.getElementById('pn-product-data');
    if (!el) return;
    var data;
    try { data = JSON.parse(el.textContent); } catch (_) { return; }

    fetchOverrides(function (ovr) {
      // ── Check overrides first ────────────────────────────────────────
      var override = (data.handle && ovr) ? ovr[data.handle] : null;

      if (override && override.mode === 'disable') {
        return; // player stays hidden for this product
      }

      if (override && override.mode === 'force' && override.tracks && override.tracks.length) {
        var forceTracks = override.tracks.map(function (t) {
          return Object.assign({}, t, {
            artist: t.artist || override.artist || data.artist || data.vendor,
            record: override.album  || data.album || data.title,
          });
        });
        window.ParanoisePlayer.enqueue(forceTracks);
        renderTracklist(forceTracks, override.album || data.album || data.title);
        return;
      }

      // ── Normal catalog + Worker flow ─────────────────────────────────
      fetchCatalog(function (cat) {
        var entry = lookupCatalog(cat, data.title, data.vendor, data.handle, data.artist);
        if (entry && entry.tracks && entry.tracks.length) {
          var cleanTitle  = entry.title  || data.title;
          var cleanArtist = entry.artist || data.artist || data.vendor;
          var allTracks = entry.tracks.map(function (t) {
            return Object.assign({}, t, {
              artist: t.artist || cleanArtist,
              record: cleanTitle,
              url: isPlayableUrl(t.url) ? t.url : '',
            });
          });
          var hasAnyUrl = allTracks.some(function (t) { return t.url; });
          if (hasAnyUrl) {
            window.ParanoisePlayer.enqueue(allTracks);
            renderTracklist(allTracks, cleanTitle);
          } else {
            resolveTracklistAvailability(allTracks, cleanArtist, cleanTitle, function (resolved) {
              window.ParanoisePlayer.enqueue(resolved);
              renderTracklist(resolved, cleanTitle);
            });
          }
        } else if (data.artist || data.vendor) {
          var _artist = data.artist || data.vendor;
          var _album  = data.album  || data.title;

          if (data.discogsId) {
            // Products imported via Disconnect have an authoritative Discogs tracklist — use it first
            fetchDiscogsTracklist(data.discogsId, function(discogsTracks, discogsAlbum, discogsArtist) {
              if (discogsTracks.length) {
                var resolveArtist = discogsArtist || _artist;
                var resolveAlbum  = discogsAlbum  || _album;
                resolveTracklistAvailability(discogsTracks, resolveArtist, resolveAlbum, function(resolved) {
                  window.ParanoisePlayer.enqueue(resolved);
                  renderTracklist(resolved, resolveAlbum);
                });
                return;
              }
              // Discogs fetch failed — try iTunes then description
              fetchItunesAlbum(_artist, _album, function(itunesTracks) {
                if (itunesTracks.length) {
                  window.ParanoisePlayer.enqueue(itunesTracks);
                  renderTracklist(itunesTracks, itunesTracks[0].record || _album);
                  return;
                }
                var descTracks = parseDescriptionTracks(data.description, _artist, _album);
                if (descTracks.length) {
                  resolveTracklistAvailability(descTracks, _artist, _album, function(resolved) {
                    window.ParanoisePlayer.enqueue(resolved);
                    renderTracklist(resolved, _album);
                  });
                } else {
                  renderTracklist([{ side: 'A', title: _album, artist: _artist, record: _album, url: '', unavailable: true }], _album);
                }
              });
            });
          } else {
            // No Discogs ID — try Deezer Worker + iTunes for previews / tracklist
            var workerParams = 'artist=' + encodeURIComponent(_artist)
              + '&album=' + encodeURIComponent(_album)
              + '&track=';
            fetch(DEEZER_WORKER + '?' + workerParams)
              .then(function (r) { return r.json(); })
              .then(function (d) {
                var deezerPreview = d.preview || '';
                var matchedArtist = d.matchedArtist || _artist;
                var matchedAlbum  = d.matchedAlbum  || _album;

                fetchItunesAlbum(matchedArtist, matchedAlbum, function(itunesTracks) {
                  if (itunesTracks.length) {
                    window.ParanoisePlayer.enqueue(itunesTracks);
                    renderTracklist(itunesTracks, itunesTracks[0].record || matchedAlbum);
                    return;
                  }
                  // iTunes failed — description tracklist is embedded in most products, use it
                  var descTracks = parseDescriptionTracks(data.description, _artist, _album);
                  if (descTracks.length) {
                    resolveTracklistAvailability(descTracks, _artist, _album, function(resolved) {
                      window.ParanoisePlayer.enqueue(resolved);
                      renderTracklist(resolved, _album);
                    });
                    return;
                  }
                  // Last resort: show track info with preview if available, unavailable row otherwise
                  var fallback = { artist: matchedArtist, record: matchedAlbum, title: matchedAlbum, side: 'A', url: deezerPreview || '', unavailable: !deezerPreview };
                  if (deezerPreview) window.ParanoisePlayer.enqueue([fallback]);
                  renderTracklist([fallback], matchedAlbum);
                });
              })
              .catch(function () {
                fetchItunesAlbum(_artist, _album, function(itunesTracks) {
                  if (itunesTracks.length) {
                    window.ParanoisePlayer.enqueue(itunesTracks);
                    renderTracklist(itunesTracks, itunesTracks[0].record || _album);
                    return;
                  }
                  var descTracks = parseDescriptionTracks(data.description, _artist, _album);
                  if (descTracks.length) {
                    resolveTracklistAvailability(descTracks, _artist, _album, function(resolved) {
                      window.ParanoisePlayer.enqueue(resolved);
                      renderTracklist(resolved, _album);
                    });
                  } else {
                    renderTracklist([{ side: 'A', title: _album, artist: _artist, record: _album, url: '', unavailable: true }], _album);
                  }
                });
              });
          }
        }
      });
    });
  }

  var PLAY_ICON  = '<svg class="pn-icon--play" width="7" height="9" viewBox="0 0 7 9" fill="none"><path d="M0 0L7 4.5L0 9V0Z" fill="currentColor"/></svg>';
  var PAUSE_ICON = '<svg class="pn-icon--pause" width="7" height="9" viewBox="0 0 7 9" fill="none"><rect width="2.5" height="9" fill="currentColor"/><rect x="4.5" width="2.5" height="9" fill="currentColor"/></svg>';

  function renderTracklist(tracks, record) {
    var container = document.getElementById('pn-tracklist-mount');
    if (!container) return;

    var html = '<div class="pn-tracklist">';
    tracks.forEach(function (t) {
      var unavailable = !!t.unavailable;
      html += '<div class="pn-tracklist-item">';
      html += '<button class="pn-track-trigger' + (unavailable ? ' is-unavailable' : '') + '"'
        + ' data-url="'    + esc(t.url)    + '"'
        + ' data-title="'  + esc(t.title)  + '"'
        + ' data-artist="' + esc(t.artist) + '"'
        + ' data-record="' + esc(record)   + '"'
        + ' data-side="'   + esc(t.side)   + '"'
        + (unavailable ? ' disabled aria-disabled="true"' : '')
        + ' aria-label="' + (unavailable ? 'No preview available' : 'Play ' + esc(t.title || record)) + '">'
        + PLAY_ICON + PAUSE_ICON
        + '</button>';
      html += '<span class="pn-tracklist-side">'  + esc(t.side)  + '</span>'
        + '<span class="pn-tracklist-title">' + esc(t.title || record) + '</span>'
        + (t.duration ? '<span class="pn-tracklist-duration">' + esc(t.duration) + '</span>' : '')
        + '</div>';
    });
    html += '</div>';
    container.innerHTML = html;
  }

  function esc(s) {
    return (s || '').replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;');
  }

  // ── Drag-to-snap (desktop only) ───────────────────────────────────────
  var isDragging   = false;
  var dragOffsetX  = 0;

  function isMobile() { return window.innerWidth <= 749; }

  function applyAnchor(anchor, animate) {
    player.classList.remove('pn-anchored-left', 'pn-anchored-right');

    if (anchor === 'left') {
      if (animate) player.style.transition = '';
      player.classList.add('pn-anchored-left');
      player.style.left      = '16px';
      player.style.transform = '';
    } else if (anchor === 'right') {
      if (animate) player.style.transition = '';
      player.classList.add('pn-anchored-right');
      player.style.left      = Math.max(16, window.innerWidth - player.offsetWidth - 16) + 'px';
      player.style.transform = '';
    } else {
      // Center — animate left to the equivalent pixel, then switch to CSS
      if (animate) {
        player.style.transition = '';
        var cx = window.innerWidth / 2 - player.offsetWidth / 2;
        player.style.left      = cx + 'px';
        player.style.transform = 'translateY(0)';
        player.addEventListener('transitionend', function onSnap(ev) {
          if (ev.propertyName !== 'left') return;
          player.removeEventListener('transitionend', onSnap);
          player.style.transition = 'none';
          player.style.left       = '';
          player.style.transform  = '';
          void player.offsetHeight;
          player.style.transition = '';
        });
      } else {
        player.style.left      = '';
        player.style.transform = '';
      }
    }
    try { sessionStorage.setItem(ANCHOR_KEY, anchor || 'center'); } catch (_) {}
  }

  function restoreAnchor() {
    var anchor;
    try { anchor = sessionStorage.getItem(ANCHOR_KEY); } catch (_) {}
    if (anchor === 'left' || anchor === 'right') {
      player.style.transition = 'none';
      applyAnchor(anchor, false);
      void player.offsetHeight;
      player.style.transition = '';
    }
  }

  function initDrag() {
    player.addEventListener('mousedown', function (e) {
      if (isMobile()) return;
      if (e.target.closest('button, input, canvas')) return;
      e.preventDefault();

      var rect    = player.getBoundingClientRect();
      dragOffsetX = e.clientX - rect.left;
      isDragging  = true;

      player.classList.remove('pn-anchored-left', 'pn-anchored-right');
      player.style.left      = rect.left + 'px';
      player.style.transform = 'translateY(0)';
      player.style.transition = 'none';
      player.classList.add('is-dragging');
    });

    document.addEventListener('mousemove', function (e) {
      if (!isDragging) return;
      var w      = player.offsetWidth;
      var newLeft = e.clientX - dragOffsetX;
      // Allow dragging up to half the player width off either edge
      newLeft = Math.max(-w * 0.5, Math.min(window.innerWidth - w * 0.5, newLeft));
      player.style.left = newLeft + 'px';
    });

    document.addEventListener('mouseup', function () {
      if (!isDragging) return;
      isDragging = false;
      player.classList.remove('is-dragging');

      var rect    = player.getBoundingClientRect();
      var centerX = rect.left + rect.width / 2;
      var vw      = window.innerWidth;

      var anchor = centerX < vw / 3 ? 'left' : centerX > 2 * vw / 3 ? 'right' : 'center';
      applyAnchor(anchor, true);
    });

    // Keep right-anchored player at the right edge when the window is resized
    window.addEventListener('resize', function () {
      if (isMobile()) {
        player.classList.remove('pn-anchored-left', 'pn-anchored-right');
        player.style.left      = '';
        player.style.transform = '';
        return;
      }
      var anchor;
      try { anchor = sessionStorage.getItem(ANCHOR_KEY); } catch (_) {}
      if (anchor === 'right') {
        player.style.left = Math.max(16, window.innerWidth - player.offsetWidth - 16) + 'px';
      }
    });
  }

  // ── Init ──────────────────────────────────────────────────────────────
  function init() {
    player = document.getElementById(PLAYER_ID);
    if (!player) return;

    canvas         = player.querySelector('.pn-waveform');
    btnPlay        = player.querySelector('[data-pn="play"]');
    btnPrev        = player.querySelector('[data-pn="prev"]');
    btnNext        = player.querySelector('[data-pn="next"]');
    pitchSlider    = player.querySelector('[data-pn="pitch"]');
    pitchRateLabel = player.querySelector('[data-pn="pitch-rate"]');
    elSide         = player.querySelector('.pn-side');
    elTitle        = player.querySelector('.pn-title');
    elMeta         = player.querySelector('.pn-meta');
    elTime         = player.querySelector('.pn-time');
    btnPlayCompact = player.querySelector('[data-pn="play-compact"]');
    elCompactTitle = player.querySelector('.pn-compact-title');

    if (btnPlay) btnPlay.addEventListener('click', function () {
      isPlaying ? doPause() : doPlay();
    });
    if (btnPlayCompact) btnPlayCompact.addEventListener('click', function () {
      isPlaying ? doPause() : doPlay();
    });
    if (btnPrev) btnPrev.addEventListener('click', playPrev);
    if (btnNext) btnNext.addEventListener('click', playNext);

    // Minimize / expand toggle
    player.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-pn="minimize"]');
      if (!btn) return;
      player.classList.toggle('is-minimized');
      try { sessionStorage.setItem('pn_minimized', player.classList.contains('is-minimized') ? '1' : '0'); } catch (_) {}

      // If right-anchored, recalculate left for the new size so the player
      // doesn't overflow the right edge during or after the transition.
      var anchor;
      try { anchor = sessionStorage.getItem(ANCHOR_KEY); } catch (_) {}
      if (anchor === 'right') {
        var targetMaxW = player.classList.contains('is-minimized') ? 340 : 880;
        var targetW    = Math.min(targetMaxW, window.innerWidth - 32);
        player.style.left = Math.max(16, window.innerWidth - targetW - 16) + 'px';
      }
    });

    // Close (mobile)
    player.addEventListener('click', function (e) {
      if (!e.target.closest('[data-pn="close"]')) return;
      player.classList.remove('is-visible');
      document.body.classList.remove('pn-active');
    });

    if (pitchSlider) {
      pitchSlider.addEventListener('input', function () {
        setPitch(parseFloat(pitchSlider.value));
        persist();
      });
      pitchSlider.addEventListener('dblclick', function () {
        setPitch(1.0);
        persist();
      });
    }

    // Waveform canvas as seek bar
    if (canvas) {
      canvas.addEventListener('click', function (e) {
        if (!audio.duration) return;
        var r   = canvas.getBoundingClientRect();
        var pct = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
        audio.currentTime = pct * audio.duration;
      });
    }

    audio.addEventListener('timeupdate', updateSeek);
    audio.addEventListener('ended',      playNext);

    // Delegated play trigger for product tracklist buttons
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.pn-track-trigger');
      if (!btn) return;
      window.ParanoisePlayer.play({
        url:    btn.dataset.url,
        title:  btn.dataset.title,
        artist: btn.dataset.artist,
        record: btn.dataset.record,
        side:   btn.dataset.side,
      });
    });

    initDrag();
    restoreAnchor();

    startViz();
    syncPlayBtn();
    restore();

    autoLoadProductTracks();

    window.addEventListener('beforeunload', persist);
    window.addEventListener('resize', resizeCanvas);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// Make entire product card clickable — delegated, skips real links/buttons
document.addEventListener('click', function (e) {
  const card = e.target.closest('.product-card-wrapper, .card-wrapper');
  if (!card) return;
  if (e.target.closest('a, button, form')) return;
  const link = card.querySelector('a[href*="/products/"]');
  if (link) window.location.href = link.href;
});
